/*
 * VideoTestPage — temporary field diagnostic at /video-test.
 * Plays every hero encode side by side in the visitor's real browser, shows a
 * live status readout, and posts the result to /api/diag/video so the exact
 * failing codec can be identified without asking the user to copy anything.
 */
import { useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CANDIDATES = [
  { key: "mp4_1080_high", label: "1080p H.264 (High) — den forsiden bruger pa desktop", src: "/hero-video-1080.mp4", type: "video/mp4" },
  { key: "webm_1080_vp9", label: "1080p VP9 / WebM — codec-fallback", src: "/hero-video-1080.webm", type: "video/webm" },
  { key: "mp4_720_high", label: "720p H.264 — bruges pa smalle skaerme", src: "/hero-video.mp4", type: "video/mp4" },
  { key: "mp4_1080_baseline", label: "1080p H.264 (Baseline) — maks. kompatibilitet", src: "/hero-video-compat.mp4", type: "video/mp4" },
];

const Probe = ({ item, onResult }) => {
  const ref = useRef(null);
  const [state, setState] = useState({ status: "indlaeser...", detail: "" });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let done = false;

    const snapshot = () => ({
      key: item.key,
      src: item.src,
      currentSrc: el.currentSrc,
      readyState: el.readyState,
      networkState: el.networkState,
      paused: el.paused,
      currentTime: Number(el.currentTime.toFixed(2)),
      duration: Number.isFinite(el.duration) ? Number(el.duration.toFixed(2)) : null,
      videoWidth: el.videoWidth,
      errorCode: el.error?.code ?? null,
      errorMsg: el.error?.message ?? null,
    });

    const finish = (ok, status, detail) => {
      if (done) return;
      done = true;
      setState({ status, detail });
      onResult({ ...snapshot(), ok, verdict: status });
    };

    const onPlaying = () => {
      const t0 = el.currentTime;
      setTimeout(() => {
        if (el.currentTime > t0) finish(true, "AFSPILLER", `${el.videoWidth}x${el.videoHeight} @ ${el.currentTime.toFixed(1)}s`);
        else finish(false, "GAAR I STAA", `frozen at ${el.currentTime.toFixed(2)}s`);
      }, 1500);
    };
    const onError = () => {
      const err = el.error;
      if (!err) return;
      finish(false, "FEJLER", `media error ${err.code}${err.message ? ` - ${err.message}` : ""}`);
    };

    el.addEventListener("playing", onPlaying);
    el.addEventListener("error", onError);
    el.play()?.catch?.((e) => finish(false, "AUTOPLAY BLOKERET", String(e?.name || e)));

    const timeout = setTimeout(() => finish(false, "TIMEOUT", `readyState ${el.readyState}, network ${el.networkState}`), 14000);

    return () => {
      clearTimeout(timeout);
      el.removeEventListener("playing", onPlaying);
      el.removeEventListener("error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tone =
    state.status === "AFSPILLER" ? "text-emerald-400 border-emerald-500/40" :
    state.status === "indlaeser..." ? "text-sky-300 border-sky-500/30" :
    "text-red-400 border-red-500/40";

  return (
    <div className={`rounded-xl border bg-white/[0.03] p-4 ${tone}`} data-testid={`probe-${item.key}`}>
      <p className="font-mono text-[11px] uppercase tracking-widest">{state.status}</p>
      <p className="mt-1 text-sm text-white">{item.label}</p>
      <p className="font-mono text-[11px] text-white/45 break-all">{item.src} · {state.detail || "-"}</p>
      <video
        ref={ref}
        className="mt-3 w-full aspect-video rounded-lg bg-black object-cover"
        muted
        loop
        playsInline
        preload="auto"
        controls
        data-testid={`probe-video-${item.key}`}
      >
        <source src={item.src} type={item.type} />
      </video>
    </div>
  );
};

export default function VideoTestPage() {
  const [results, setResults] = useState({});
  const [sent, setSent] = useState(null);
  const resultsRef = useRef({});

  useEffect(() => {
    const t = setTimeout(async () => {
      const el = document.createElement("video");
      const report = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        vendor: navigator.vendor,
        screen: `${window.screen.width}x${window.screen.height}`,
        innerWidth: window.innerWidth,
        devicePixelRatio: window.devicePixelRatio,
        reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
        saveData: navigator.connection?.saveData ?? null,
        effectiveType: navigator.connection?.effectiveType ?? null,
        canPlay: {
          mp4: el.canPlayType("video/mp4"),
          avc1High: el.canPlayType('video/mp4; codecs="avc1.640028"'),
          avc1Main: el.canPlayType('video/mp4; codecs="avc1.4d401f"'),
          avc1Baseline: el.canPlayType('video/mp4; codecs="avc1.42E01E"'),
          webmVp9: el.canPlayType('video/webm; codecs="vp9"'),
          hevc: el.canPlayType('video/mp4; codecs="hvc1"'),
        },
        probes: resultsRef.current,
      };
      try {
        await fetch(`${API}/diag/video`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(report),
        });
        setSent(true);
      } catch {
        setSent(false);
      }
    }, 17000);
    return () => clearTimeout(t);
  }, []);

  const record = (r) => {
    resultsRef.current = { ...resultsRef.current, [r.key]: r };
    setResults(resultsRef.current);
  };

  return (
    <main className="min-h-screen bg-[#05070c] text-white px-5 py-14" data-testid="video-test-page">
      <div className="mx-auto max-w-5xl">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--cyan)]">Video diagnostik</p>
        <h1 className="mt-3 font-display font-semibold text-4xl sm:text-5xl tracking-tight">
          Hvilken video kan din browser afspille?
        </h1>
        <p className="mt-5 text-base text-white/70 max-w-2xl leading-relaxed">
          Lad denne side vaere aaben i ca. 20 sekunder. Alle fire kandidater testes samtidig, og resultatet
          sendes automatisk til udvikleren. Gron = afspiller, rod = fejler.
        </p>

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {CANDIDATES.map((item) => (
            <Probe key={item.key} item={item} onResult={record} />
          ))}
        </div>
        <div className="mt-10 rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="diag-status">
          <p className="font-mono text-[11px] uppercase tracking-widest text-white/50">Rapport</p>
          <p className="mt-2 text-sm">
            {sent === true && <span className="text-emerald-400">Rapport sendt. Du kan lukke siden.</span>}
            {sent === false && <span className="text-red-400">Kunne ikke sende rapporten (netvaerksfejl).</span>}
            {sent === null && <span className="text-white/60">Samler data... sendes automatisk efter 17 sekunder.</span>}
          </p>
          <pre className="mt-4 overflow-x-auto font-mono text-[11px] leading-relaxed text-white/50">
{JSON.stringify(results, null, 2)}
          </pre>
        </div>
      </div>
    </main>
  );
}
